import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { transformOverlay } from "./imageOverlays";

import { dragScrollOffset, usePointerDragAutoScroll } from "../../shared/dragAutoScroll";

export default function EditableImageOverlay({ overlay, selected, disabled, onSelect, onCommit, onInteracting, imageStyle, accessibleLabel }) {
  const { start: startAutoScroll, stop: stopAutoScroll } = usePointerDragAutoScroll();
  const drag = useRef(null);
  const [draft, setDraft] = useState(null);
  useEffect(() => () => onInteracting(false), [onInteracting]);
  useEffect(() => {
    if (disabled && drag.current) {
      stopAutoScroll();
      drag.current = null;
      setDraft(null);
      onInteracting(false);
    }
  }, [disabled, onInteracting, stopAutoScroll]);
  const current = draft || overlay;
  const finish = (event, cancel = false) => {
    if (!drag.current) return;
    event.stopPropagation();
    const { start, clientX, clientY, rect, corner, originScroll, root } = drag.current;
    const scroll = dragScrollOffset(root);
    const next = transformOverlay(start, (event.clientX - clientX + scroll.x - originScroll.x) / rect.width, (event.clientY - clientY + scroll.y - originScroll.y) / rect.height, corner);
    stopAutoScroll();
    drag.current = null;
    setDraft(null);
    onInteracting(false);
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (!cancel && ["x", "y", "width", "height"].some((key) => next[key] !== start[key])) onCommit(next);
  };
  return <Frame
    data-image-overlay
    role="button" aria-label={accessibleLabel || `覆蓋圖片：${overlay.label}`} aria-pressed={selected}
    tabIndex={disabled ? -1 : 0} $selected={selected && !disabled} $disabled={disabled}
    style={{ left: `${current.x * 100}%`, top: `${current.y * 100}%`, width: `${current.width * 100}%`, height: `${current.height * 100}%` }}
    onClick={(event) => { event.stopPropagation(); if (!disabled) onSelect(); }}
    onKeyDown={(event) => {
      if (disabled) return;
      if (event.key === "Enter" || event.key === " ") { event.stopPropagation(); event.preventDefault(); onSelect(); }
      const delta = { ArrowLeft: [-.002, 0], ArrowRight: [.002, 0], ArrowUp: [0, -.002], ArrowDown: [0, .002] }[event.key];
      if (delta) { event.stopPropagation(); event.preventDefault(); onSelect(); onCommit(transformOverlay(overlay, ...delta)); }
    }}
    onPointerDown={(event) => {
      event.stopPropagation();
      if (disabled || (event.button !== undefined && event.button !== 0)) return;
      const root = event.currentTarget.parentElement;
      const imageRect = root.querySelector("[data-preview-base]")?.getBoundingClientRect();
      const rect = imageRect?.width ? imageRect : root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      event.preventDefault(); onSelect(); onInteracting(true);
      event.currentTarget.focus({ preventScroll: true });
      event.currentTarget.setPointerCapture?.(event.pointerId);
      drag.current = { start: overlay, clientX: event.clientX, clientY: event.clientY, rect, root, originScroll: dragScrollOffset(root), corner: event.target.dataset.corner || "move" };
      startAutoScroll(event);
    }}
    onPointerMove={(event) => {
      if (!drag.current) return;
      event.stopPropagation(); event.preventDefault();
      const { start, clientX, clientY, rect, corner, originScroll, root } = drag.current;
      const scroll = dragScrollOffset(root);
      setDraft(transformOverlay(start, (event.clientX - clientX + scroll.x - originScroll.x) / rect.width, (event.clientY - clientY + scroll.y - originScroll.y) / rect.height, corner));
    }}
    onPointerUp={(event) => finish(event)} onPointerCancel={(event) => finish(event, true)}
    onLostPointerCapture={(event) => finish(event, true)}
  >
    <img src={overlay.image} alt="" draggable={false} style={imageStyle?.(current)} />
    {selected && !disabled && ["nw", "ne", "sw", "se"].map((corner) => <Handle key={corner} data-corner={corner}
      aria-label={`縮放圖片 ${corner}`} $corner={corner} />)}
  </Frame>;
}

const Frame = styled.div`
  position: absolute;
  touch-action: none;
  cursor: ${({ $disabled }) => $disabled ? "default" : "move"};
  outline: ${({ $selected }) => $selected ? "2px solid #2563eb" : "none"};
  outline-offset: 1px;
  pointer-events: ${({ $disabled }) => $disabled ? "none" : "auto"};
  img { display: block; width: 100%; height: 100%; pointer-events: none; }
`;
const Handle = styled.span`
  position: absolute; width: 12px; height: 12px; background: white; border: 2px solid #2563eb;
  box-sizing: border-box; border-radius: 2px;
  top: ${({ $corner }) => $corner.includes("n") ? "-6px" : "auto"};
  bottom: ${({ $corner }) => $corner.includes("s") ? "-6px" : "auto"};
  left: ${({ $corner }) => $corner.includes("w") ? "-6px" : "auto"};
  right: ${({ $corner }) => $corner.includes("e") ? "-6px" : "auto"};
  cursor: ${({ $corner }) => ["nw", "se"].includes($corner) ? "nwse-resize" : "nesw-resize"};
`;
